import { z } from "zod"
import { LegacyCompatibleDiscountSchema } from "./discount"
import { DiscountExclusionsSchema } from "./discount-code"
import {
  FreshServiceProposalSchema,
  SubmitFreshServiceProposalSchema,
} from "./fresh-service-proposal"
import { PartGrade } from "./part-grade"
import { ProposalAbTestFlagsSchema } from "./proposal-ab-test"
import { ProposalLevelPresetsSchema } from "./proposal-level"
import { PartCategory } from "./service-bike-part"
import { ServiceAction } from "./service-part-action"
import { GenerateServiceProposalPreviewSchema } from "./service-proposal"
import { EquipmentAnswerSchema } from "./upsell"

/**
 * View-model layer for the redesigned customer-facing service proposal.
 *
 * The proposal endpoint only returns the parts a mechanic proposed an action
 * for. The new UX additionally shows the full inspection ("Cykelskick") and a
 * summary of what was agreed at drop-off ("Inlämning"), so the screen takes a
 * bundle rather than the bare proposal. Each piece is
 * intentionally flat and presentation-shaped: the source data already exists
 * (`ServiceBikeCondition` via `Route.SERVICE_BIKE__GET_CONDITION`, `Reception`
 * via `Route.RECEPTION_CONTROLLER__FIND_ONE_BY_JOB_ID`), and mapping into these
 * shapes is the caller's job.
 */

export const ProposalBikeComponentSchema = z.object({
  partId: z.string(),
  partName: z.string(),
  partImage: z.string(),
  grade: z.nativeEnum(PartGrade),
  /** Null when the mechanic proposed no action for this component. */
  recommendedAction: z.nativeEnum(ServiceAction).nullable(),
  actionLabel: z.string().optional(),
  comment: z.string().nullable(),
  /** Why a component that doesn't work needs attention. Null when it works. */
  functionNotice: z.string().nullable().default(null),
})
export type ProposalBikeComponent = z.infer<typeof ProposalBikeComponentSchema>

export const ProposalBikeConditionCategorySchema = z.object({
  category: z.nativeEnum(PartCategory),
  components: z.array(ProposalBikeComponentSchema),
})
export type ProposalBikeConditionCategory = z.infer<
  typeof ProposalBikeConditionCategorySchema
>

export const ProposalBikeConditionSchema = z.object({
  mechanicName: z.string(),
  categories: z.array(ProposalBikeConditionCategorySchema),
})
export type ProposalBikeCondition = z.infer<typeof ProposalBikeConditionSchema>

export const ReceptionSummaryActionSchema = z.object({
  title: z.string(),
  /** Drives which icon the row gets. */
  kind: z.enum(["PACKAGE", "WASH", "PART", "MANUAL"]),
})
export type ReceptionSummaryAction = z.infer<
  typeof ReceptionSummaryActionSchema
>

export const ReceptionSummarySchema = z.object({
  bike: z.object({
    typeLabel: z.string(),
    brand: z.string().nullable(),
    color: z.string().nullable(),
  }),
  actions: z.array(ReceptionSummaryActionSchema),
  receivedAt: z.coerce.date(),
  storeName: z.string(),
  receivedByName: z.string(),
  customerBudget: z.number().nullable(),
  comment: z.string().nullable(),
})
export type ReceptionSummary = z.infer<typeof ReceptionSummarySchema>

export const ServiceProposalContextSchema = z.object({
  proposal: FreshServiceProposalSchema,
  bikeCondition: ProposalBikeConditionSchema,
  reception: ReceptionSummarySchema,
})
export type ServiceProposalContext = z.infer<
  typeof ServiceProposalContextSchema
>

export const ProposalDiscountsSchema = z.object({
  laborDiscount: LegacyCompatibleDiscountSchema.nullable(),
  productsDiscount: LegacyCompatibleDiscountSchema.nullable(),
  orderDiscount: LegacyCompatibleDiscountSchema.nullable(),
  /** Takes the place of `orderDiscount` while the customer approves everything. */
  fullApprovalOrderDiscount: LegacyCompatibleDiscountSchema.nullable(),
  /** What the job's discount code keeps at full price. */
  exclusions: DiscountExclusionsSchema,
})
export type ProposalDiscounts = z.infer<typeof ProposalDiscountsSchema>

/**
 * What `Route.SERVICE_PROPOSAL__GET_CONTEXT` returns: the render bundle plus the
 * few things the screen needs that the proposal itself does not carry. Kept
 * separate from `ServiceProposalContext` because `ServiceProposalScreenProps`
 * extends that type and declares `discounts` as an optional prop of its own.
 */
export const ServiceProposalPageDataSchema =
  ServiceProposalContextSchema.extend({
    discounts: ProposalDiscountsSchema,
    /** Keyed by `originalPackageId`; the package snapshot carries these, the proposal does not. */
    packageDescriptions: z.record(z.string(), z.string()),
    /** The customer has already answered — render the accepted screen, not the choices. */
    isAlreadyAccepted: z.boolean(),
    /**
     * What the customer last had, so a reload picks up where they left off.
     * Null until they change anything.
     */
    savedSelection: SubmitFreshServiceProposalSchema.nullable(),
    /**
     * The proposal as the mechanic sent it, from the immutable
     * `SentServiceProposal` snapshot. "Återställ" goes back to this — never to
     * the defaults of a proposal the optimizer has since rewritten.
     */
    originalSelection: SubmitFreshServiceProposalSchema,
    /**
     * How the customer answered, for the view-only look back at the proposal.
     * Null until they have answered, and for an answered proposal whose answer
     * could not be recovered.
     */
    answeredSelection: SubmitFreshServiceProposalSchema.nullable(),
    /**
     * The mechanic marked some work as able to wait, so the customer is offered
     * "Trafiksäker" and "Toppskick" as two proposals. Null when there is
     * nothing to choose between, or the proposal was answered without a level.
     */
    levelPresets: ProposalLevelPresetsSchema.nullable(),
    /** What the job's A/B test variants show and hide. */
    abTestFlags: ProposalAbTestFlagsSchema,
  })
export type ServiceProposalPageData = z.infer<
  typeof ServiceProposalPageDataSchema
>

/**
 * What the inspection's "Sammanfattning" step asks for: the ordinary preview
 * plus what the summary shows the way the customer will see it.
 */
export const GenerateInspectionProposalPreviewSchema =
  // The backend reads the job's leasing company labels itself.
  GenerateServiceProposalPreviewSchema.omit({ leasingCompanyIds: true }).extend(
    {
      jobId: z.string().uuid(),
      /** Who is named on "Cykelskick"; null until the mechanic is picked. */
      mechanicId: z.string().uuid().nullable(),
      equipment: z.array(EquipmentAnswerSchema),
    },
  )
export type GenerateInspectionProposalPreview = z.infer<
  typeof GenerateInspectionProposalPreviewSchema
>

export const InspectionUpsellSummarySchema = z.object({
  /** Categories the mechanic offers tillval in. */
  offeredCategoryCount: z.number(),
  /**
   * The recommended products' total, fitting and the "here and now" discount
   * included. The service budget does not cover it.
   */
  recommendedTotal: z.number(),
})
export type InspectionUpsellSummary = z.infer<
  typeof InspectionUpsellSummarySchema
>

/**
 * The customer's proposal screen as it will look once sent, for the mechanic.
 * Nothing in it is saved.
 */
export const InspectionProposalPreviewSchema =
  ServiceProposalContextSchema.extend({
    /** Keyed by `originalPackageId`. */
    packageDescriptions: z.record(z.string(), z.string()),
    upsellSummary: InspectionUpsellSummarySchema,
    /** As the customer will get them; clicking them changes nothing saved. */
    levelPresets: ProposalLevelPresetsSchema.nullable(),
    /** Where the customer will start: Toppskick when there are levels. */
    originalSelection: SubmitFreshServiceProposalSchema,
  })
export type InspectionProposalPreview = z.infer<
  typeof InspectionProposalPreviewSchema
>
